import binIcon from '@src/main/assets/icons/bin-red.svg';
import editIcon from '@src/main/assets/icons/edit-red.svg';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './AppTable.module.scss';

type Header = {
  name: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  field: keyof any;
  width?: string;
};

type SelectionChangeEvent<T> = {
  originalEvent: React.SyntheticEvent;
  value: T[];
};

type AppTableProps<T> = {
  headers: Header[];
  data: T[];
  visibility?: boolean[];
  onEdit?: (row: T) => void;
  onDelete?: (row: T) => void;
  onSelectedChange?: (selected: T[]) => void;
  disableActions?: (row: T) => boolean;
  defaultSortField?: string;
  defaultSortOrder?: 1 | -1;
  filter?: boolean;
};

const AppTable = <T extends { id: string | number }>({
  headers,
  data,
  visibility = [],
  onEdit,
  onDelete,
  onSelectedChange,
  disableActions,
  defaultSortField,
  defaultSortOrder = 1,
  filter = true,
}: AppTableProps<T>) => {
  const { t } = useTranslation();
  const [selectedRows, setSelectedRows] = React.useState<T[]>([]);

  useEffect(() => {
    const remainingSelections = selectedRows.filter((selected) =>
      data.some((item) => item.id === selected.id),
    );

    if (remainingSelections.length !== selectedRows.length) {
      setSelectedRows(remainingSelections);
      onSelectedChange?.(remainingSelections);
    }
  }, [data, onSelectedChange, selectedRows]);

  const actionBodyTemplate = (rowData: T) => {
    const shouldHideActions = disableActions?.(rowData);
    if (shouldHideActions) return null;

    return (
      <div className="flex gap-2">
        {onEdit && (
          <Button
            icon={<img src={editIcon} alt={t('common.edit')} width="15" />}
            onClick={() => onEdit(rowData)}
            tooltip={t('common.edit')}
          />
        )}
        {onDelete && (
          <Button
            icon={<img src={binIcon} alt={t('common.delete')} width="15" />}
            className="p-button-text"
            onClick={() => onDelete(rowData)}
            tooltip={t('common.delete')}
          />
        )}
      </div>
    );
  };

  const handleSelectionChange = (e: SelectionChangeEvent<T>) => {
    const selected = e.value || [];
    setSelectedRows(selected);
    onSelectedChange?.(selected);
  };

  return (
    <DataTable
      value={data}
      selectionMode="checkbox"
      selection={selectedRows}
      onSelectionChange={handleSelectionChange}
      dataKey="id"
      rows={50}
      rowsPerPageOptions={[10, 20, 50]}
      paginator
      sortField={defaultSortField}
      sortOrder={defaultSortOrder}
    >
      <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} />
      {headers.map((col, index) =>
        visibility[index] !== false ? (
          <Column
            key={String(col.field)}
            field={String(col.field)}
            header={col.name}
            filter={filter}
            filterField={String(col.field)}
            showAddButton={false}
            sortable
            style={col.width ? { width: col.width } : undefined}
          />
        ) : null,
      )}
      {(onEdit || onDelete) && <Column body={actionBodyTemplate} header={t('common.actions')} />}
    </DataTable>
  );
};

export default AppTable;
